/**
 * React, react redux and react localization imports
 */
import { useDispatch, useSelector } from 'react-redux'

import { logout } from '../../../api/auth'

/**
 * Material imports
 */
import {
  Logout as LogoutIcon,
  Menu as MenuIcon,
  ThumbUpOutlined
} from '@mui/icons-material'
import {
  Avatar,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem, Typography
} from '@mui/material'
/**
 * Relative exports to root directory
 */
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant'
import SendIcon from '@mui/icons-material/Send'
import { Box } from '@mui/system'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toggleMenu } from '../../../state/menu'
import { useThemeMode } from '../../../theme'
import { avatarUrl } from '../../../utils'
import { Button } from './Button'
import { ChecklistIcon } from './ChecklistIcon'
import { TranslatableTypography } from './Typography'

const getButtonThemeSx = (palette) => ({
  button: {
    padding: '5px',
    borderRadius: '15px',
    height: '100%'
  },
  icon: {
    marginLeft: '10px',
    marginRight: '10px',
    color: palette.textColor,
    height: '100%'
  },
  image: {
    height: '100%',
    filter: `drop-shadow(0px 0px 3px ${palette.headerBrightColor})`
  }
})

/**
 * Component for opening hamburger menu
 * It's connected to redux state management.
 */
export const HamburgerDrawerButton = (props) => {
  const { referrence } = props
  const toggled = useSelector((state) => state?.menu)[referrence] ?? false
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { palette } = useThemeMode()
  const themeSX = getButtonThemeSx(palette)

  const control = {
    onClick: (_e) => {
      dispatch(toggleMenu({ referrence, value: !toggled }))
    }
  }

  return (
    <IconButton
      size="large"
      aria-label="menu"
      style={themeSX.button}
      {...control}
    >
      <MenuIcon style={themeSX.icon} />
      <img
        className="highlight-shadow"
        style={themeSX.image}
        alt="Convention logo"
        src={t('header')}
      />
    </IconButton>
  )
}

/**
 * This is main popup menu component
 */
export const HamburgerDrawerMenu = (props) => {
  const { referrence } = props
  const { palette } = useThemeMode()
  const toggled = useSelector((state) => state?.menu)[referrence] ?? false
  const dispatch = useDispatch()

  const control = {
    onClose: () => dispatch(toggleMenu({ referrence, value: !toggled }))
  }

  return (
    <Drawer anchor="left" open={toggled} {...control}>
      <Box
        sx={{
          width: '250px',
          height: '100%',
          background: palette.headerColor
        }}
      >
        {props.children}
      </Box>
    </Drawer>
  )
}

/**
 * Component used for holding all the items
 */
export const TranslatableMenuItem = () => {
  const { t } = useTranslation()

  return (
    <MenuItem>
      {t(this.props.text)}
      {this.props.children}
    </MenuItem>
  )
}

export const UserDrawerMenuItem = (props) => {
  const { link, icon, label, onClick, className } = props
  const { palette } = useThemeMode()

  const userItemStyle = {
    wrapper: {
      padding: '0px !important',
      color: palette.paperColor,
      fontWeight: 600
    },
    icon: {
      minWidth: 'auto',
      color: palette.paperColor,
      fontWeight: 600,
      marginRight: '5px'
    }
  }

  return (
    <ListItem className={className} sx={userItemStyle.wrapper}>
      <ListItemButton
        onClick={() => onClick(link)}
        component={Link}
        to={link}
      >
        <ListItemIcon sx={userItemStyle.icon}>
          {icon}
        </ListItemIcon>
        <ListItemText>
          <TranslatableTypography>{label}</TranslatableTypography>
        </ListItemText>
      </ListItemButton>
    </ListItem>
  )
}

export const UserDrawerMenuItems = (props) => {
  const { attendee, avatar_url, nick, referrence } = props
  const { palette } = useThemeMode()
  const { config } = useSelector((state) => state?.config)
  const { auth } = useSelector((state) => state?.auth)
  const dispatch = useDispatch()

  const menuItemsStyle = {
    loginWrapper: {
      width: '100%',
      padding: '10px'
    },
    avatarWrapper: {
      display: 'inline-block'
    },
    nickWrapper: {
      marginLeft: '10px',
      display: 'inline-block',
      fontWeight: 600,
      color: palette.paperColor
    },
    logoutWrapper: {
      width: '100%',
      textAlign: 'center',
      marginTop: '15px'
    },
    menuList: {
      padding: '0px !important',
      '& .companion-menu-item .MuiListItemIcon-root': {
        width: '35px',
        '& svg': {
          display: 'block',
          margin: '0 auto'
        }
      }
    },
    fullWidth: {
      width: '100%'
    }
  }

  const onLogout = () => {
    logout(config, auth)
    // this.closeDrawer()
    dispatch(toggleMenu({ referrence, value: false }))
  }

  const closeDrawer = () => {
    dispatch(toggleMenu({ referrence, value: false }))
  }

  const avatar = avatarUrl(config, avatar_url)

  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      sx={menuItemsStyle.fullWidth}
    >
      <Grid
        item
        sx={{
          ...menuItemsStyle.loginWrapper,
          ...menuItemsStyle.fullWidth
        }}
      >
        <Box sx={menuItemsStyle.avatarWrapper}>
          <Avatar alt={nick} src={avatar} />
        </Box>
        <Box sx={menuItemsStyle.nickWrapper}>
          <Typography>{nick}</Typography>
          <Typography>#{attendee}</Typography>
        </Box>
      </Grid>

      <Grid item sx={menuItemsStyle.fullWidth}>
        <List sx={menuItemsStyle.menuList}>
          <Divider />
          <UserDrawerMenuItem className="companion-menu-item" icon={ <ThumbUpOutlined /> } onClick={closeDrawer} label="voting" link="/voting" />
          <Divider />
          <UserDrawerMenuItem icon={ <ChecklistIcon /> } onClick={closeDrawer} label="my-achievements" link="/achievements/user" />
          <Divider />
          <UserDrawerMenuItem className="companion-menu-item" icon={ <SendIcon /> } onClick={closeDrawer} label="yaps" link="/yaps" />
          <Divider />
          <UserDrawerMenuItem className="companion-menu-item" icon={ <TableRestaurantIcon /> } onClick={closeDrawer} label="snacks-title" link="/voting/snacks" />
          <Divider />
        </List>
      </Grid>

      <Grid
        item
        sx={{
          ...menuItemsStyle.logoutWrapper,
          ...menuItemsStyle.fullWidth
        }}
      >
        <Button
          component={Link}
          to="/logout"
          variant="invert-contained"
          startIcon={<LogoutIcon />}
          onClick={onLogout}
        >
          logout
        </Button>
      </Grid>
    </Grid>
  )
}
